import styled from "@emotion/styled";
import type { PublicKey } from "@solana/web3.js";
import { deformatNumber, formatNumberToLocale, TagInfo } from "@stepfinance/react";
import { Tooltip } from "antd";
import type { FC } from "react";

import { useLanguageContext } from "../../contexts/language/language";
import type { CurrencyContextState } from "../../contexts/swap";
import { getTokenName } from "../../utils/tokenInfo";
import { formatStringWithPlaceholders } from "../../utils/utils";
import { HeaderDiv } from "../AmountInput/AmountInput";
import RedirectLink from "../RedirectLink/RedirectLink";
import { NumberLabel } from "../shared";
import { SwapTokenSelector } from "../SwapAmountInput/SwapTokenSelector";
import { UserMintBalance } from "../UserMintBalance/UserMintBalance";

type OutTokenDisplayProps = {
  B: CurrencyContextState;
  A: CurrencyContextState;
  isWrapping: boolean;
  onMintChange?: (mint: PublicKey) => void;
};

export const OutTokenDisplay: FC<OutTokenDisplayProps> = ({ B, A, isWrapping, onMintChange }) => {
  const {
    currentLanguageData: {
      common,
      pages: {
        dashboard: {
          modules: { rewardOptions },
        },
        swapLP: swapText,
      },
    },
  } = useLanguageContext();

  const tooltipContent = (
    <div>
      {swapText.swapBlurb}{" "}
      <RedirectLink href="https://docs.step.finance/the-step-token/step-reward-options">
        {common.learnMoreLink}
      </RedirectLink>
    </div>
  );

  const parsedAAmount = deformatNumber(A.amount);
  const displayZero = isNaN(parsedAAmount) || parsedAAmount === 0;

  return (
    <>
      <HeaderDiv>
        <span className="amount-header">
          {formatStringWithPlaceholders(common.amountInputLabels.youWill, common.amountInputLabels.receive)}
        </span>
        <UserMintBalance mint={B.mintAddress} />
      </HeaderDiv>
      <OutTokenBox>
        <SwapTokenSelector
          onMintChange={onMintChange}
          mintAddress={B.mintAddress}
          symbol={getTokenName(B.mintAddress.toBase58())}
        />
        {isWrapping ? (
          <OutAmountDisplay>
            {displayZero
              ? "0.00"
              : formatNumberToLocale(parsedAAmount, {
                  maximumFractionDigits: 9,
                })}
          </OutAmountDisplay>
        ) : (
          <TagInfo tagType="text" uppercase={false} useSpaceMono={false}>
            <Tooltip className="tooltip" placement="bottom" title={tooltipContent}>
              + {rewardOptions.title}
            </Tooltip>
          </TagInfo>
        )}
      </OutTokenBox>
    </>
  );
};

const OutAmountDisplay = styled(NumberLabel)`
  font-size: 18px;
  font-weight: 700;
`;

const OutTokenBox = styled.div`
  border: 1px solid rgba(120, 120, 120, 0.15);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  width: 100%;
  padding: 16px 10px;
`;
